import Text from '@shared/components/content/text';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@shared/components/ui/chart';
import { ReactNode } from 'react';
import { Bar, BarChart, XAxis, YAxis } from 'recharts';

/**
 * Scope series colours — the chart palette's teal ramp, per the design.
 * The bg-chart-* Tailwind classes wrap these vars in hsl() while the vars
 * hold hex values, so reference the vars directly, the same way chart
 * configs do.
 */
export const SCOPE_BAR_COLORS: Record<string, string> = {
  '1': 'var(--chart-1)',
  '2': 'var(--chart-2)',
  '3': 'var(--chart-3)',
};

const SCOPE_LEGEND = [
  { label: 'Scope 3', color: SCOPE_BAR_COLORS['3'] },
  { label: 'Scope 2', color: SCOPE_BAR_COLORS['2'] },
  { label: 'Scope 1', color: SCOPE_BAR_COLORS['1'] },
];

/** Swatch legend shown above a bar list — one entry per series. */
export function BarLegend({
  items,
}: {
  items: { label: string; color: string }[];
}) {
  return (
    <div className="flex items-center gap-4">
      {items.map(({ label, color }) => (
        <span key={label} className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm" style={{ background: color }} />
          <Text variant="subtle" className="text-xs">
            {label}
          </Text>
        </span>
      ))}
    </div>
  );
}

/** Swatch legend for the three GHG scopes, shown above scope bar lists. */
export function ScopeLegend() {
  return <BarLegend items={SCOPE_LEGEND} />;
}

export interface EmissionsBarRow {
  key: string;
  label: ReactNode;
  /** One segment renders a plain bar; several render a stacked bar. */
  segments: { color: string; tonnes: number | null }[];
  /** Formatted value for the right-hand readout; null renders an em dash. */
  readout: string | null;
}

interface EmissionsBarListProps {
  rows: EmissionsBarRow[];
  /** Names for each segment, used in tooltip / a11y. Order matches `segments`. */
  seriesNames?: string[];
  /** Width of the sibling label column on the left, in pixels. */
  labelColumnWidth?: number;
}

/**
 * Horizontal bar-per-row breakdown used by the emissions dashboards
 * (per organisation, per category). Bars are sized relative to the
 * largest row total. Recharts under the hood for tooltips + a11y; the
 * row labels are rendered as a sibling HTML column on the left so they
 * stay left-aligned and the bars sit flush against the chart's left edge.
 */
export function EmissionsBarList({
  rows,
  seriesNames,
  labelColumnWidth = 180,
}: EmissionsBarListProps) {
  const seriesCount = rows[0]?.segments.length ?? 0;
  const seriesColors = Array.from(
    { length: seriesCount },
    (_, index) => rows[0]?.segments[index]?.color ?? 'var(--chart-1)'
  );
  const resolvedNames = Array.from(
    { length: seriesCount },
    (_, index) => seriesNames?.[index] ?? `Series ${index + 1}`
  );

  const chartConfig: ChartConfig = Object.fromEntries(
    Array.from({ length: seriesCount }, (_, index) => [
      `series_${index}`,
      { label: resolvedNames[index], color: seriesColors[index] },
    ])
  );

  const data = rows.map((row) => {
    const flat: Record<string, number | string> = {
      label: typeof row.label === 'string' ? row.label : row.key,
    };
    row.segments.forEach((segment, index) => {
      flat[`series_${index}`] = segment.tonnes ?? 0;
    });
    return flat;
  });

  const height = Math.max(rows.length * 32 + 24, 120);
  const chartMargin = { top: 8, right: 16, bottom: 8, left: 0 };

  return (
    <div className="flex w-full items-stretch gap-4" style={{ height }}>
      <RowLabelsColumn
        rows={rows}
        chartMargin={chartMargin}
        width={labelColumnWidth}
      />
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-full min-w-0 flex-1"
      >
        <BarChart
          accessibilityLayer
          data={data}
          layout="vertical"
          margin={chartMargin}
          barCategoryGap="20%"
        >
          <XAxis type="number" hide />
          <YAxis dataKey="label" type="category" hide />
          <ChartTooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.3 }}
            content={
              <ChartTooltipContent
                valueFormatter={(value) => `${Number(value).toFixed(2)} tCO₂e`}
              />
            }
          />
          {Array.from({ length: seriesCount }, (_, index) => {
            const isFirst = index === 0;
            const isLast = index === seriesCount - 1;
            // Horizontal stacked bars: leftmost segment rounds its left
            // edge (top-left + bottom-left); rightmost rounds its right
            // edge; inner edges stay square so segments butt flush.
            // Radius array is [topLeft, topRight, bottomRight, bottomLeft].
            const radius: [number, number, number, number] = [
              isFirst ? 4 : 0,
              isLast ? 4 : 0,
              isLast ? 4 : 0,
              isFirst ? 4 : 0,
            ];
            return (
              <Bar
                key={index}
                dataKey={`series_${index}`}
                stackId="a"
                fill={seriesColors[index]}
                name={resolvedNames[index]}
                radius={radius}
                isAnimationActive={false}
              />
            );
          })}
        </BarChart>
      </ChartContainer>
    </div>
  );
}

export interface ComparisonBarRow {
  key: string;
  label: ReactNode;
  /**
   * Bars rendered top-to-bottom within the row; each is scaled independently
   * against the largest value in its own column (so two different metrics —
   * e.g. emissions vs weight — can share a row without one dwarfing the other).
   */
  bars: { color: string; value: number | null }[];
  /** Formatted right-hand readout value; null renders an em dash. */
  readout: string | null;
  /** Unit shown after the readout, e.g. "kgCO₂e/kg". */
  readoutUnit?: string;
  /** Small caption under the readout, e.g. "intensity". */
  readoutCaption?: string;
}

interface ComparisonBarListProps {
  rows: ComparisonBarRow[];
  /** Names for each bar index, used in tooltip / a11y. Order matches `bars`. */
  seriesNames?: string[];
  /** Width of the sibling label column on the left, in pixels. */
  labelColumnWidth?: number;
}

/**
 * Horizontal bar-per-row breakdown that renders several
 * independently-scaled bars per row (e.g. emissions vs purchased
 * weight). Each series is pre-normalised to its column max so the bars
 * share a 0–100% chart space; the tooltip restores the raw values from
 * each row's payload. Sibling to {@link EmissionsBarList}, which stacks
 * segments on a single scale.
 */
export function ComparisonBarList({
  rows,
  seriesNames,
  labelColumnWidth = 180,
}: ComparisonBarListProps) {
  const seriesCount = rows[0]?.bars.length ?? 0;
  const columnMaxes = Array.from({ length: seriesCount }, (_, index) =>
    Math.max(1, ...rows.map((row) => row.bars[index]?.value ?? 0))
  );
  const seriesColors = Array.from(
    { length: seriesCount },
    (_, index) => rows[0]?.bars[index]?.color ?? 'var(--chart-1)'
  );
  const resolvedNames = Array.from(
    { length: seriesCount },
    (_, index) => seriesNames?.[index] ?? `Series ${index + 1}`
  );

  const chartConfig: ChartConfig = Object.fromEntries(
    Array.from({ length: seriesCount }, (_, index) => [
      `series_${index}_pct`,
      { label: resolvedNames[index], color: seriesColors[index] },
    ])
  );

  const data = rows.map((row) => {
    const flat: Record<string, number | string> = {
      label: typeof row.label === 'string' ? row.label : row.key,
    };
    row.bars.forEach((bar, index) => {
      flat[`series_${index}_pct`] =
        ((bar.value ?? 0) / columnMaxes[index]) * 100;
      flat[`series_${index}_value`] = bar.value ?? 0;
    });
    return flat;
  });

  // Two bars per row + spacing — slightly more vertical room than the stacked variant.
  const height = Math.max(rows.length * 40 + 24, 140);
  const chartMargin = { top: 8, right: 16, bottom: 8, left: 0 };

  return (
    <div className="flex w-full items-stretch gap-4" style={{ height }}>
      <RowLabelsColumn
        rows={rows}
        chartMargin={chartMargin}
        width={labelColumnWidth}
      />
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-full min-w-0 flex-1"
      >
        <BarChart
          accessibilityLayer
          data={data}
          layout="vertical"
          margin={chartMargin}
          barCategoryGap="25%"
        >
          <XAxis type="number" hide domain={[0, 100]} />
          <YAxis dataKey="label" type="category" hide />
          <ChartTooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.3 }}
            content={
              <ChartTooltipContent
                formatter={(_value, _name, item) => {
                  const match = String(item.dataKey).match(/series_(\d+)_pct/);
                  if (!match) return null;
                  const seriesIndex = Number(match[1]);
                  const raw = (item.payload as Record<string, number>)[
                    `series_${seriesIndex}_value`
                  ];
                  return (
                    <span className="flex w-full items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5">
                        <span
                          className="size-2.5 rounded-sm"
                          style={{ background: seriesColors[seriesIndex] }}
                        />
                        {resolvedNames[seriesIndex]}
                      </span>
                      <span className="font-mono text-xs">
                        {raw.toFixed(2)}
                      </span>
                    </span>
                  );
                }}
              />
            }
          />
          {Array.from({ length: seriesCount }, (_, index) => (
            <Bar
              key={index}
              dataKey={`series_${index}_pct`}
              fill={seriesColors[index]}
              name={resolvedNames[index]}
              radius={[4, 4, 4, 4]}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ChartContainer>
    </div>
  );
}

/**
 * Sibling left column rendering each row's label, aligned to the chart's
 * category bands. Labels are absolutely positioned at the exact band
 * centers — Recharts distributes its YAxis categories evenly between
 * the chart margins, so a label that wraps to two lines (e.g. a long
 * org name) musn't push its siblings off-band. Using absolute keeps
 * each label anchored to its own row regardless of content height.
 */
function RowLabelsColumn({
  rows,
  chartMargin,
  width,
}: {
  rows: { key: string; label: ReactNode }[];
  chartMargin: { top: number; bottom: number };
  width: number;
}) {
  return (
    <div className="relative shrink-0" style={{ width }}>
      {rows.map((row, index) => {
        const total = rows.length;
        // Recharts distributes category bands evenly between margin.top
        // and margin.bottom. Each band's center sits at the midpoint of
        // its band — match that with percentages keyed off the same
        // start/end so labels track bar rows even when the chart resizes.
        const topPct = (index / total) * 100;
        const heightPct = (1 / total) * 100;
        return (
          <div
            key={row.key}
            className="absolute left-0 right-0 flex items-center pr-2"
            style={{
              top: `calc(${chartMargin.top}px + ${topPct}% - ${
                (chartMargin.top + chartMargin.bottom) * (index / total)
              }px)`,
              height: `calc(${heightPct}% - ${
                (chartMargin.top + chartMargin.bottom) / total
              }px)`,
            }}
          >
            {typeof row.label === 'string' ? (
              <SimpleTooltip
                side="top"
                trigger={
                  <span className="block w-full truncate text-sm text-gray-900">
                    {row.label}
                  </span>
                }
                content={row.label}
              />
            ) : (
              row.label
            )}
          </div>
        );
      })}
    </div>
  );
}
